import type { Currency, ReferralSettingsRecord } from '@navio/nsdk';
import { revalidateLogic } from '@tanstack/react-form';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiSheet, useAppForm } from 'noddi-ui-web';
import { z } from 'zod';
import { appliesToModeFromLists, type AppliesToModeValues } from '../appliesToMode';
import { DISCOUNT_KIND } from '../discountKind';
import { resolvePromotionCopy, type PromotionCopyLocale } from '../suggestedPromotionCopy';
import { invalidateBrandReferralQueries } from './invalidateBrandReferralQueries';
import { ReferralDiscountFormFields } from './ReferralDiscountFormFields';
import { templateToReferralDiscountPayload, toReferralDiscountCreate } from './referralDiscountPayload';

const FORM_ID = 'add-invite-discount-form';

const emptyAppliesToMode: AppliesToModeValues = appliesToModeFromLists({
  productCount: 0,
  tagCount: 0
});

const validationSchema = z
  .object({
    name: z.string(),
    description: z.string(),
    discountKind: z.enum([DISCOUNT_KIND.amount, DISCOUNT_KIND.percentage]),
    discountAmount: z.number().nullable(),
    discountPercentage: z.number().nullable(),
    salesItemIds: z.array(z.number()),
    salesItemTagIds: z.array(z.number()),
    appliesToMode: z.object({
      appliesToAllProducts: z.boolean(),
      filterByProducts: z.boolean(),
      filterByTags: z.boolean()
    })
  })
  .superRefine((value, ctx) => {
    if (value.discountKind === 'amount' && (value.discountAmount == null || value.discountAmount <= 0)) {
      ctx.addIssue({ code: 'custom', message: 'Enter a discount amount', path: ['discountAmount'] });
    }
    if (
      value.discountKind === 'percentage' &&
      (value.discountPercentage == null || value.discountPercentage <= 0 || value.discountPercentage > 100)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Enter a percentage between 1 and 100',
        path: ['discountPercentage']
      });
    }
  });

type FormValues = z.input<typeof validationSchema>;

type Props = {
  open: boolean;
  onClose: () => void;
  brandId: number;
  serviceOrganizationId?: number;
  settings: ReferralSettingsRecord;
  currency: Currency;
  locale: PromotionCopyLocale;
};

export const AddInviteDiscountSheet = ({
  open,
  onClose,
  brandId,
  serviceOrganizationId,
  settings,
  currency,
  locale
}: Props) => {
  const invalidateInput = serviceOrganizationId != null ? { brandId, serviceOrganizationId } : { brandId };

  const { mutateAsync: patchSettings } = noddiAsync.usePatch({
    type: URLKeys.patchBrandReferralSettings,
    queryConfig: {
      onSuccess: async () => {
        await invalidateBrandReferralQueries(invalidateInput);
        onClose();
      }
    }
  });

  const form = useAppForm({
    // oxlint-disable-next-line typescript/no-unnecessary-type-assertion -- useAppForm infers the form type from defaultValues - without the cast discountKind widens to string and the Zod validator no longer fits (TS2322)
    defaultValues: {
      name: '',
      description: '',
      discountKind: 'amount',
      discountAmount: null,
      discountPercentage: null,
      salesItemIds: [],
      salesItemTagIds: [],
      appliesToMode: emptyAppliesToMode
    } as FormValues,
    validationLogic: revalidateLogic(),
    validators: { onDynamic: validationSchema },
    onSubmit: async ({ value }) => {
      const { name, description } = resolvePromotionCopy({
        name: value.name,
        description: value.description,
        discountKind: value.discountKind,
        discountAmount: value.discountAmount,
        discountPercentage: value.discountPercentage,
        appliesToAllProducts: value.appliesToMode.appliesToAllProducts,
        currency,
        locale
      });
      if (!name) {
        return;
      }
      await patchSettings({
        brandId,
        serviceOrganizationId,
        inviteDiscounts: [
          ...settings.inviteDiscounts.map((template) =>
            toReferralDiscountCreate(templateToReferralDiscountPayload(template))
          ),
          toReferralDiscountCreate({
            name,
            description,
            salesItemIds: value.salesItemIds,
            salesItemTagIds: value.salesItemTagIds,
            discount:
              value.discountKind === 'amount' && value.discountAmount != null
                ? { amount: value.discountAmount, currency }
                : null,
            discountPercentage: value.discountKind === 'percentage' ? value.discountPercentage : null
          })
        ]
      });
    }
  });

  return (
    <form.AppForm>
      <NoddiSheet
        open={open}
        onOpenChange={(next) => !next && onClose()}
        title='Add invite discount'
        description='Issued to referred customers when they activate a referral link.'
        footer={<form.SubscribeButton formId={FORM_ID} label='Save' className='w-full' />}
      >
        <form
          id={FORM_ID}
          className='flex flex-col gap-5'
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <ReferralDiscountFormFields
            form={form}
            currency={currency}
            locale={locale}
            nameField={{
              name: 'name',
              label: 'Name',
              tooltip: 'Shown on the coupon the customer receives.'
            }}
            descriptionField={{
              name: 'description',
              label: 'Description',
              tooltip: 'Short explanation of this discount for customers.'
            }}
          />
        </form>
      </NoddiSheet>
    </form.AppForm>
  );
};
